import React from 'react';
import type { FilterSEOData } from '../../types';

export const FILTERED_STD_COPY: FilterSEOData = {
  summer: {
    description:
      "Set the tone for your big day with Zola's summer save the dates. Bright templates perfect for any celebration. Free guest addressing and envelopes!",
    summary: (
      <>
        <p>
          Who doesn’t love a summer wedding? From the warm weather and bright colors to the
          easygoing atmosphere, summertime is the perfect season for new beginnings and endless
          love.
        </p>

        <p>
          While the season may be easy and breezy, Zola’s{' '}
          <a href="/wedding-planning" target="_blank" rel="noopener noreferrer">
            wedding planning
          </a>{' '}
          services are meticulous and comprehensive—exactly what you need for your dream wedding.
          Let’s start with the first step on your checklist:{' '}
          <a href="/wedding-planning/invitations/shop" target="_blank" rel="noopener noreferrer">
            wedding invitations
          </a>
          .
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'How do we plan a summer wedding with save the dates to match?',
        body: (
          <>
            <p>
              Summer{' '}
              <a
                href="/wedding-vendors/search/wedding-venues"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding venues
              </a>{' '}
              can range from beachy fun to whimsical wonder or lush greenery, and you’ll want save
              the dates to match. To plan the ultimate celebration, check out these seasonal
              suggestions to incorporate your wedding invitation into any summer wedding:
            </p>
            <p>
              <strong>Beach & Destination</strong> – A tried-and-true classic, beach weddings
              encapsulate everything we love about summertime. Reflecting the warmth of the sand,
              the cool tones of the water, and the airy feeling of beach attire is easy with summer
              save the dates in light tones, complete with subtle detailing like seashells, palm
              trees, and ocean waves.
            </p>
            <p>
              <strong>Floral & Botanical</strong> – Embrace the vivid colors of summer blossoms with
              bright floral invitations to match or play on the classic greenery of outdoor garden
              weddings with simple vine detailing and subtle floral embellishments. This wedding
              invitation idea is perfect for the invitation card, the bridal shower invitation, and
              the cocktail party invite as well.
            </p>
            <p>
              <strong>Bohemian & Whimsical</strong> – Summer is perfect for outdoor venues like
              rustic ranches and secret gardens. Find similarly beautiful summer invitations that
              embody the simple yet playful essence of your theme, including minimal detailing,
              funky fonts, pastel hues, and creative composition.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we design the right summer wedding save the dates?',
        body: (
          <>
            <p>
              You can create invitations that are as true to you as they are to the latest summer
              staples with Zola’s stunning designs and customization options:
            </p>
            <p>
              <strong>Color</strong> – Beat the heat by ditching the deep winter tones. Instead, add
              some pops of color to your invitations with bright oranges or beachy blues. With
              Zola’s selection of colorful designs, you’ll find a color palette that works for your
              vision and the season.
            </p>
            <p>
              <strong>Custom Photo</strong> – Showcase your summer lovin’ by adding a sunkissed
              photo to your save the dates. Zola has plenty of photograph-ready templates as well as
              DIY options that can perfectly frame your favorite outdoor picture of the two of you.
            </p>
            <p>
              <strong>Foil</strong> – Mimic the sun’s rays with Zola’s gold or rose gold foil
              embellishments. These metallic details will add a touch of warmth and sophistication
              to your summer save the dates.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we find the best save the dates for a summer wedding?',
        body: (
          <>
            <p>
              Summer is the season of relaxation, so why not take a load off this wedding season?
              Zola has hundreds of summer invitation templates, gorgeous color palettes, and
              customization options to prepare you with everything you need for your warm-weather
              wedding, starting with the perfect{' '}
              <a
                href="/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>
              , custom{' '}
              <a href="/wedding-registry" target="_blank" rel="noopener noreferrer">
                wedding registry
              </a>
              , and free{' '}
              <a href="/wedding-planning/website" target="_blank" rel="noopener noreferrer">
                wedding website
              </a>
              .
            </p>
          </>
        ),
      },
    ],
  },
  winter: {
    description:
      "Zola's winter wedding invitation designs are a distinguished, seasonal way to invite cherished ones to your celebration. Shop now with free guest addressing!",
    summary: (
      <p>
        Winter is a time of elegance, celebration, and of course, love. Putting together a winter
        wedding is a magical experience—if you know what you’re doing. Fortunately, anyone can put
        together a winter wedding straight out of wonderland when they use Zola. We have all the
        resources you need to plan a stylish wedding, including hundreds of beautiful winter-themed{' '}
        <a href="/wedding-planning/save-the-date/shop" target="_blank" rel="noopener noreferrer">
          save the dates
        </a>
        .
      </p>
    ),
    faqContent: [
      {
        subheading: 'How do we incorporate stylish save the dates into our winter wedding?',
        body: (
          <>
            <p>
              Your wedding deserves the perfect winter wonderland save the dates. There are many
              standard winter staples and themes that make for both a beautiful wedding and a save
              the date design:
            </p>
            <p>
              <strong>Classic & Formal</strong> - Winter is the perfect time for black-tie formal,
              grand opulence, and elaborate decor. For save the dates that are as elegant and classy
              as the wedding itself, consider a black-and-white color scheme or rich jewel tones.
              Formal invites often include cursive fonts, simple composition, and minimal frills.
            </p>
            <p>
              <strong>Cozy Bohemian</strong> - Beyond traditional holiday trimmings, winter weddings
              have a variety of options for decor. Many couples put a seasonal twist on their
              centerpieces with berries, pinecones, and birch bark, and decorate their{' '}
              <a
                href="/wedding-vendors/search/wedding-venues"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding venues
              </a>{' '}
              with wooden accents and warm lights. Instead of floral accents, consider greenery from
              an evergreen tree. You can add these elements to your invitations with Zola’s rustic,
              vintage, and boho designs.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we craft the perfect winter save the date for our wedding?',
        body: (
          <>
            <p>
              Just like snowflakes, every winter wedding is different. With Zola’s wide array of
              designs and customization options, you can create{' '}
              <a
                href="/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>{' '}
              as individual as the falling snow:
            </p>
            <p>
              <strong>Color</strong> - Some couples opt for the classic crystalized look, embodying
              the cool white and blue tones of snow. Others may choose bolder hues, including deep
              reds, earthy greens, and white accents. Whichever path you choose, Zola has a wide
              selection of colorful save the date designs to match any palette you have selected for
              your wedding day.
            </p>
            <p>
              <strong>Foil</strong> - Winter is one of the few times you’re allowed to mix silver
              and gold, so you might as well take advantage of it. Add a touch of sparkle to your
              save the dates with Zola’s gorgeous foil embellishments in silver, gold, and rose
              gold.
            </p>
            <p>
              <strong>Custom Photo</strong> - Have you been <i>walking in a winter wonderland</i>{' '}
              recently—with elegant engagement photos to prove it? Zola lets you add your favorite
              festive photos to many save the date designs, as well as to our custom DIY templates.
            </p>
          </>
        ),
      },
      {
        subheading: 'Where can we find the best selection of winter wedding save the dates?',
        body: (
          <>
            <p>
              For the perfect snow-covered celebration, choose from hundreds of beautiful winter
              wedding date design templates. With Zola, couples can also find matching wedding
              programs, set up a{' '}
              <a href="/wedding-registry" target="_blank" rel="noopener noreferrer">
                wedding registry
              </a>
              , and even create a free{' '}
              <a href="/wedding-planning/website" target="_blank" rel="noopener noreferrer">
                wedding website
              </a>
              .
            </p>
            <p>
              It may be cold outside, but there’s nothing to warm your soul like{' '}
              <a href="/wedding-planning" target="_blank" rel="noopener noreferrer">
                wedding planning
              </a>{' '}
              with the love of your life—and we’re not talking about your spouse-to-be, we’re
              talking about Zola.
            </p>
          </>
        ),
      },
    ],
  },
  spring: {
    description:
      "Set the tone for your big day with Zola's spring save the dates. Spring-inspired templates perfect for any celebration. Get free guest addressing!",
    summary: (
      <>
        <p>There’s something in the air this spring, and no, it’s not pollen. It’s love!</p>

        <p>
          Spring weddings are a classic choice, and for good reason. The season kindles feelings of
          renewal, optimism, and joy—weddings are the perfect combination of all three. The first
          step to bringing this celebration to life? Finding your spring{' '}
          <a href="/wedding-planning/save-the-date/shop" target="_blank" rel="noopener noreferrer">
            save the dates
          </a>{' '}
          at Zola.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'How do we plan a spring wedding with matching save the dates?',
        body: (
          <>
            <p>
              By definition, any wedding between March and June is a spring wedding, but it’s about
              more than the date itself. It’s a feeling. So how do you embody that breezy springtime
              ethos?
            </p>
            <p>
              Here are some tips for capturing the essence of spring, including in your save the
              dates:
            </p>
            <p>
              <strong>Floral & Botanical</strong> – Flowers are a classic choice for spring wedding
              decor. Lush greenery, draping garlands, and blooming bouquets look stunning against
              any springtime backdrop. Incorporating floral and botanical designs into your spring
              wedding save the dates will tie the whole event together with ease.
            </p>
            <p>
              <strong>Whimsical & Bohemian</strong> – Secret gardens, hanging lanterns, and the
              woodsy enchantment of a whimsical wedding are all perfect for spring. If your{' '}
              <a href="/wedding-planning" target="_blank" rel="noopener noreferrer">
                wedding planning
              </a>{' '}
              consists of fairy lights, wooden furniture, and twine details, then you’ll love the
              delicate accents and natural hues of our bohemian save the dates.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we choose the perfect spring wedding save the dates?',
        body: (
          <>
            <p>
              Spring weddings are beautiful, and they deserve the perfect invitations to match.
              Capture the delicate tone of this beloved season with these stylish spring wedding
              invitation ideas and potential customizations:
            </p>
            <p>
              <strong>Color</strong> – Spring is all about the return of color. From delicate
              pastels to earthy greens, your color palette will have a huge effect on your decor,
              florals, and of course, your invites. Find save the dates with your favorite spring
              shades, like lilac, peach, and yellow.
            </p>
            <p>
              <strong>Paper Type</strong> – Okay, maybe spring isn’t just about color—it’s also
              about shedding layers. Rejoice in the warm weather by adding light, airy touches to
              your wedding decor and to save the dates with Zola’s lightweight linen paper option.
            </p>
            <p>
              <strong>Foil</strong> – Hints of gold or rose gold will give your invitations a warm,
              inviting feel. This is an easy way to add elegance and style to your date design,
              without overloading the page. Zola users can add these metallic elements to almost any
              design.
            </p>
          </>
        ),
      },
      {
        subheading: 'Where’s the best place to find spring wedding stationery?',
        body: (
          <>
            <p>
              A spring wedding brings new beginnings—Zola brings your new beginnings to life with
              our tried-and-true save the dates and{' '}
              <a
                href="/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>
              . With a wide selection of eye-catching designs, you’ll have plenty of spring
              stationery ideas to choose from. From floral to fresh, and everything in between,
              we’ve got all the styles you need.
            </p>
            <p>What are you waiting for? It’s time to spring into action.</p>
          </>
        ),
      },
    ],
  },
  fall_autumn: {
    description:
      "Set the tone for your big day with Zola's fall save the dates. Cozy, crisp templates perfect for any celebration. Get free guest addressing and more!",
    summary: (
      <p>
        From the rich colors to the crisp air, nothing beats an autumn day full of celebration and
        love. Planning the perfect fall wedding will take some serious style inspiration, but that’s
        where Zola comes in. We have all the tools you need to bring your big day to life, including
        hundreds of templates for fall wedding{' '}
        <a href="/wedding-planning/save-the-date/shop" target="_blank" rel="noopener noreferrer">
          save the dates
        </a>
        .
      </p>
    ),
    faqContent: [
      {
        subheading: 'How do we plan a fall wedding with save the dates to match?',
        body: (
          <>
            <p>
              Autumn wedding decor can be fresh, bold, and enchanting—if you play your cards right.
              Capitalizing on these trendy autumn styles will make it easy to tie in your
              fall-themed save the dates:
            </p>
            <p>
              <strong>Botanical</strong> – For an autumn wedding, you’ll love the look of rich, cold
              weather blossoms, such as roses, dianthus, and celosias, whether they’re on your
              tables as centerpieces or your fall{' '}
              <a
                href="/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>{' '}
              as embellishments. Couples can easily match their date announcement to any arrangement
              with Zola’s wide selection of floral designs.
            </p>
            <p>
              <strong>Rustic & Vintage</strong> – Fall is the perfect season for a rustic or vintage
              wedding theme. Between the earthy tones and natural elements, you can use the season’s
              innate charm to your advantage. By the same token, Zola’s rustic and vintage designs
              will embody the rugged beauty of this picturesque season.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we design the perfect fall save the dates and invitations?',
        body: (
          <>
            <p>
              You’ll want to couple your fall wedding with a perfect set of save the dates. Whether
              it is a postcard, wedding date photo card, or a wedding invite using a date template,
              you can have the invitation of your dreams. At Zola, you can choose from hundreds of
              beautiful designs or personalize your own. Start by searching for premade favorites or
              customizing according to these fall invitation ideas:
            </p>
            <p>
              <strong>Color</strong> – Pull inspiration from the gorgeous autumn leaves for your
              wedding’s color palette. Shades of rust, maroon, and marigold will fit perfectly into
              your save the dates, the same way they do in your table settings, floral arrangements,
              and overall decor.
            </p>
            <p>
              <strong>Foil</strong> – If you’re thinking of putting an autumn twist on your
              traditional silverware colors, you can do the same with Zola’s elegant foil designs.
              Warmer tones of gold or rose gold will match the season’s natural palette and your
              autumn invites.
            </p>
            <p>
              <strong>Custom Photo</strong> – Show off your love of fall by adding a custom photo.
              Whether you’re playing in the leaves, holding hands in a pumpkin patch, or something
              equally adorable and on-theme, you can add your favorite autumn shot to many save the
              date templates.
            </p>
          </>
        ),
      },
      {
        subheading: 'Where do we find the best save the dates for every season?',
        body: (
          <>
            <p>
              Whether you’re planning for a fall, winter, spring, or summer wedding, Zola has you
              covered every day of the year. Choose from our wide selection of autumn save the
              dates, or personalize your own design, to get invitations that fit your style. While
              you’re here, we also offer wedding program templates,{' '}
              <a href="/wedding-registry" target="_blank" rel="noopener noreferrer">
                wedding registry
              </a>{' '}
              services, and even{' '}
              <a href="/wedding-planning/website" target="_blank" rel="noopener noreferrer">
                free wedding websites
              </a>
              . There’s no need to fall all over yourself planning the perfect wedding when you have
              a great{' '}
              <a
                href="/expert-advice/checklist/your-ultimate-wedding-planning-checklist"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding checklist
              </a>{' '}
              and Zola in your corner.
            </p>
          </>
        ),
      },
    ],
  },
  elegant_formal: {
    description:
      "Set the tone for your big day with Zola's elegant save the dates. Refined templates perfect for any celebration. Get free guest addressing and more!",
    summary: (
      <>
        <p>
          Sometimes, the most beautiful option is the simplest. If your refined taste is both
          sophisticated and straightforward, then an elegant wedding invitation will perfectly set
          the mood for your celebration.
        </p>
        <p>
          With dozens of luxurious and tasteful card designs, Zola can help you find your perfect
          shade of elegance. Enjoy beautiful save the dates without all the extra fuss.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'How will elegant save the dates fit into my wedding?',
        body: (
          <>
            <p>
              When planning a wedding, there’s always a period of design indecision. Between luxe
              glamor, rustic bohemian barn, fairytale romance, and everything in between, the
              options can be overwhelming. Choosing traditional elegance can be a wonderful way to
              cut through the noise and make your celebration shine:
            </p>
            <p>
              <b>Season</b> – Naturally, there’s elegance in every season. However, winter weddings
              more easily embody the minimalist, breathtaking feel of true refinement. If planning
              your wedding during late fall or winter, elegant wedding save the dates will fit the
              season.
            </p>
            <p>
              <b>Location</b> –{' '}
              <a
                href="https://www.zola.com/wedding-vendors/search/wedding-venues"
                target="_blank"
                rel="noopener noreferrer"
              >
                Wedding venues
              </a>{' '}
              often set the tone. While breweries and beaches are relaxed and joyous locales, true
              elegance shines through in formal venues. Ballrooms, museums, historic estates, and
              religious halls best fit this graceful aesthetic.
            </p>
            <p>
              <b>Style &amp; Theme</b> - Does your dream wedding resemble Cinderella’s ball more
              than Snow White’s cottage? If your wedding style is streamlined and polished, Zola can
              help you create formal, elegant{' '}
              <a
                href="https://www.zola.com/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>{' '}
              to match.
            </p>
          </>
        ),
      },
      {
        subheading: 'What else should I consider for elegant save the date cards?',
        body: (
          <>
            <p>
              Elegance isn’t one-size-fits-all. But with Zola’s custom filters, you can add elegant
              luxury to your personal style. Mix and match these flourishes to create the perfect
              card:
            </p>
            <p>
              <b>Color</b> – Elegance shines in sophisticated or muted tones—think deep greens,
              romantic blushes, and sleek neutral tones. You can start by browsing all of Zola’s
              classic and elegant styles, or search for specific color palettes.
            </p>
            <p>
              <b>Foil</b> – There’s a way to encapsulate both refined simplicity and modern sparkle.
              Touches of gold, silver, or rose gold foil can elevate invitations from plain to
              polished.
            </p>
            <p>
              <b>Custom Photo</b> – Feel free to show off your real-life elegance with a
              sophisticated couple’s photo to match. To stay in line with your desired aesthetic,
              you’ll want to use a simple and romantic picture, surrounded by sparse text in a
              monochromatic palette. A center alignment helps maintain a clean look.
            </p>
          </>
        ),
      },
      {
        subheading: 'Save the Date in Elegant Style with Zola',
        body: (
          <>
            <p>
              With Zola, you can elevate both your wedding invitations and your planning process
              with a refined simplicity. Create your perfect{' '}
              <a
                href="https://www.zola.com/wedding-planning/save-the-date/shop/elegant_formal"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>{' '}
              with our easy-to-use design filters and customizations, as well as a matching{' '}
              <a
                href="https://www.zola.com/wedding-planning/website"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding website
              </a>{' '}
              to share with friends and family.
            </p>
            <p>
              Still in the early stages of the{' '}
              <a
                href="https://www.zola.com/wedding-planning"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding planning
              </a>{' '}
              process? Zola has expert advice for all things wedding, from your guest list to{' '}
              <a
                href="https://www.zola.com/wedding-registry"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding registry
              </a>{' '}
              and venue search. With us, planning your wedding can be as simple and straightforward
              as your elegant invitation designs.
            </p>
          </>
        ),
      },
    ],
  },
  classic_traditional: {
    description:
      "Set the tone for your big day with Zola's classic and traditional save the date templates perfect for any celebration. Get free addressing and more!",
    summary: (
      <>
        <p>
          <i>Something old, something new, something borrowed, something…</i> classically timeless
          to unify the entire event, like beautiful save the date cards. Weddings are all about
          traditions, so why not extend that same principle to your{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/shop"
            target="_blank"
            rel="noopener noreferrer"
          >
            wedding invitations
          </a>
          ?
        </p>
        <p>
          If your Pinterest wedding board is filled with rose bouquets and chandeliered halls, then
          classic wedding invitations are right up your alley. And with Zola’s traditional save the
          dates, you’ll kick off your wedding year in timeless style.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'How will classic save the dates fit into my wedding?',
        body: (
          <>
            <p>
              Classic isn’t quite the same as “simple” or “neutral” styles, which can work with
              almost anything. Some weddings fit the traditional look more than others. You can
              easily pair classic save the dates with these traditional wedding details:
            </p>
            <p>
              <b>Season</b> – The best part of classic style? Its versatility. Timeless and chic,
              classic wedding save the dates work across the calendar year. However, there’s also
              something magical about a traditional fall or winter wedding, with its extravagant
              decor, formal proceedings, and black-tie finery.
            </p>
            <p>
              <b>Location</b> – Hallowed churches, lush gardens, splendid banquet halls—traditional{' '}
              <a
                href="https://www.zola.com/wedding-vendors/search/wedding-venues"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding venues
              </a>{' '}
              always have a feel of grandeur. If choosing a luxurious, upscale location, then a
              classic formal wedding invitation with a custom design will set the mood for guests.
            </p>
            <p>
              <b>Style &amp; Theme</b> – Prefer neutrals over bold colors? Carnations over baby’s
              breath and greenery? Ceremony and rituals over alternative programming? Then a
              traditional, softly romantic save the date card will fit your wedding perfectly.
            </p>
          </>
        ),
      },
      {
        subheading: 'What else should I consider when designing classic save the date cards?',
        body: (
          <>
            <p>
              You don’t have to be a world-class designer to create{' '}
              <a
                href="https://www.zola.com/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>{' '}
              that both you and your wedding guests will love. Zola can help you find and
              personalize the perfect classic invite aesthetic. Here are a few classic wedding
              invitation ideas for you to explore:
            </p>
            <p>
              <b>Color</b> – Classic save the dates often have a simple black and white color
              palette, but they don’t have to be fully monochromatic. Accent colors like rich blues,
              muted greens, and feminine pinks can all evoke the traditional wedding look.
            </p>
            <p>
              <b>Foil</b> – Who says you can’t update the aesthetic of your classic cards? Give
              traditional{' '}
              <a
                href="https://www.zola.com/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>{' '}
              a timeless but personalized feel with subtle touches, like small gold or silver foil
              embellishments.
            </p>
            <p>
              <b>Custom Photo</b> – <i>Traditionally</i>, most wedding invites were minimal,
              text-only cards. But of course, a loving picture of you and your partner can still fit
              into a traditional theme. A simple, romantic shot surrounded by elegant text will
              beautifully capture your style.
            </p>
          </>
        ),
      },
      {
        subheading: 'Traditional Wedding Save the Dates—With a Zola Twist',
        body: (
          <>
            <p>
              With Zola, classic never means boring. With so many customization options, your
              traditional wedding invitations will still feel like you. And that’s just the start.
              With your{' '}
              <a
                href="https://www.zola.com/wedding-planning/website"
                target="_blank"
                rel="noopener noreferrer"
              >
                free wedding website
              </a>
              , a{' '}
              <a
                href="https://www.zola.com/wedding-planning/checklist"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding planning checklist
              </a>
              , and all the{' '}
              <a
                href="https://www.zola.com/wedding-vendors"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding vendors
              </a>{' '}
              you need, Zola will help you plan the wedding you want.
            </p>
            <p>
              So, if you still haven’t found your something new, it’s time to scroll through our
              classic save the date card designs.
            </p>
          </>
        ),
      },
    ],
  },
  floral: {
    description:
      "Set the tone for your big day with Zola's floral save the dates. Decorative flower templates perfect for any celebration. Get free addressing!",
    summary: (
      <p>
        Between bouquets, boutonnieres, flower girls, and floral arrangements, most weddings are
        practically a traveling garden. Gorgeous centerpieces and captivating scents set the tone
        for a day of romance and beauty. If the wedding aisle of your dreams is lined with petals,
        consider{' '}
        <a
          href="https://www.zola.com/wedding-planning/invitations/shop"
          target="_blank"
          rel="noopener noreferrer"
        >
          wedding invitations
        </a>{' '}
        to match. Zola can help you create the perfect floral save the date card with our wedding
        flower inspiration.
      </p>
    ),
    faqContent: [
      {
        subheading: 'How will floral save the dates fit into my wedding?',
        body: (
          <>
            <p>
              Flowers may be a wedding staple, but some couples turn their decorative elements into
              an all-out theme. Are you fully committing to the flower power? Here’s how floral
              wedding invitations can meld with your overall wedding plans:
            </p>
            <p>
              <b>Season</b> – They don’t say <i>April showers bring May flowers</i> for nothing.
              Floral themes come to life when the flowers themselves are in full bloom—spring and
              summer weddings.{' '}
            </p>
            <p>
              <b>
                Outdoor{' '}
                <a
                  href="https://www.zola.com/wedding-vendors/search/wedding-venues"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Wedding Venues
                </a>
              </b>{' '}
              – Botanical gardens, estate grounds, rustic barns, and even backyard weddings are
              already making excellent use of the natural landscape. You can take this a step
              further by tying your theme together with wildflowers, greenery, and colorful blossoms
              on your wedding invitations.
            </p>
            <p>
              <b>Romantic &amp; Formal Styles</b> – Flowers range from effortlessly whimsical to
              pristinely formal. If your wedding fits the latter, choosing save the dates with
              classic flowers like roses, orchids, and carnations will add some color to your
              traditional wedding theme.
            </p>
          </>
        ),
      },
      {
        subheading: 'What else should I consider for floral save the date cards?',
        body: (
          <>
            <p>
              The perfect floral arrangement is a careful composition. Similarly, floral save the
              dates require the right combination of colors, fonts, and stylistic touches:
            </p>
            <p>
              <b>Color</b> – Going for lush, tropical flora at your reception? Try invites with
              bright oranges, deep pinks, and lush greens. Giving pastel carnations to the
              bridesmaids? Then search for save the dates with soft blushes and lavenders to match.
            </p>
            <p>
              <b>Custom Photo</b> – If you want to feature your relationship center stage, add your
              favorite picture of you two. Framing an engagement photo with a wreath of greenery or
              delicate vines combines personality and style, for save the dates that are
              distinctively <i>you</i>.
            </p>
            <p>
              <b>Foil &amp; Letterpress</b> – Looking to add some fun to your fancy florals?
              Invitations with delicate touches of gold or rose gold foil enhance the warmth of your
              color palette while adding a hint of sparkle. Alternatively, letterpress printing
              boosts the sophistication of a funkier flower design.
            </p>
          </>
        ),
      },
      {
        subheading: 'Let Your Wedding Style Blossom with Zola’s Floral Invitations',
        body: (
          <>
            <p>
              If your pile of wedding decisions is growing faster than a sprouting sunflower, Zola
              can help you get things under control. Design your favorite floral{' '}
              <a
                href="https://www.zola.com/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>{' '}
              using our premade styles, search functions, and customizable features. You can even
              choose five free samples from our extensive collection so you can weed out the ones
              that aren’t quite right.
            </p>
            <p>
              With Zola’s{' '}
              <a
                href="https://www.zola.com/wedding-planning"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding planning
              </a>{' '}
              assistance and{' '}
              <a
                href="https://www.zola.com/wedding-planning/website"
                target="_blank"
                rel="noopener noreferrer"
              >
                free wedding websites
              </a>
              , your big day will come to life like a garden in spring. Start planting the seeds of
              your floral wedding today!
            </p>
          </>
        ),
      },
    ],
  },
  greenery: {
    description:
      "Set the tone for your big day with Zola's greenery save the dates. Eucalyptus and botanical templates perfect for any celebration. Free addressing!",
    summary: (
      <>
        <p>
          If you’re looking for a wedding invitation that will make your guests green with envy,
          then you’ll love the natural, earthy tones and simple, subdued designs of greenery save
          the dates. Between botanical bridal flowers and a woodsy destination wedding, the curving
          vines and delicate leaves of nature save the dates will introduce your wedding with style.
        </p>
        <p>
          No green thumb required—just check out Zola’s{' '}
          <a
            href="https://www.zola.com/wedding-planning/invitations/shop"
            target="_blank"
            rel="noopener noreferrer"
          >
            wedding invitations
          </a>{' '}
          to find your perfect shade.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'What kind of weddings work best with botanical save the dates?',
        body: (
          <>
            <p>
              Hosting a luxurious Manhattan wedding in a Fifth Avenue hotel ballroom? Then earthy,
              eucalyptus save the dates would be a bold choice. If you’re going for a more
              conventional option, a greenery invite will make a perfect addition to the following
              wedding styles:
            </p>
            <p>
              <b>Whimsical &amp; Bohemian</b> – Tying the knot in a secret garden with draping
              greenery, exposed light bulbs, and wooden accents? Match the real-life greenery with
              effortlessly earthy tones on your wedding date invitation.
            </p>
            <p>
              <b>Modern</b> – Greenery is <i>in</i> these days. A modern wedding with a neutral
              color scheme and simple decor can benefit from a subtle pop of color within the leaves
              and vines of a botanical save the date.
            </p>
            <p>
              <b>Rustic &amp; Natural</b> – What’s more natural than greenery? An outdoor wedding
              with rustic, eco-friendly undertones will make use of everything nature has to
              offer—the save the dates are no exception.
            </p>
            <p>
              Not every wedding has to have a distinct wedding day theme. If you’re planning a
              simple spring or summer wedding, greenery save the dates can provide an understated
              yet appropriate color palette for a themeless wedding.
            </p>
          </>
        ),
      },
      {
        subheading: 'What else should I consider when designing my greenery save the dates?',
        body: (
          <>
            <p>
              When it comes to botanical save the dates, there’s one thing you don’t even have to
              think about: the color scheme. But beyond that, what makes a stunning greenery save
              the date?{' '}
            </p>
            <p>
              When designing your natural invitations on Zola, you can personalize them according to
              these popular stylistic choices:
            </p>
            <p>
              <b>Paper Type</b> – Why not fully embrace the idea of a <i>green</i> wedding?
              Postcard-style save the dates can be made with recycled or natural paper.
            </p>
            <p>
              <b>Style</b> – Greenery can range from elaborate intertwining vines to simple leaf
              accents adorning the corners. Find the style that works for you, whether it’s
              minimalist or romantic and botanical.
            </p>
            <p>
              <b>Rounded Corners</b> – The harsh lines of rectangular invites don’t necessarily mesh
              with the natural curvature of Mother Nature’s landscape. Opting for rounded edges is a
              simple way to elevate greenery invites.
            </p>
          </>
        ),
      },
      {
        subheading: 'Don’t Be Blue! Shop Greenery Save the Dates on Zola',
        body: (
          <>
            <p>
              Zola can help create naturally gorgeous{' '}
              <a
                href="https://www.zola.com/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>{' '}
              and wedding invites with a few clicks of a button. You can easily design your dream
              invitations online or first select up to five sample styles to see in person, free of
              charge. That way, you can finalize your favorite design long before your loved ones
              receive them in the mail.
            </p>
          </>
        ),
      },
    ],
  },
  rustic: {
    description:
      "Set the tone for your big day with Zola's rustic save the dates. Countryside templates perfect for any celebration. Free addressing and envelopes!",
    summary: (
      <>
        <p>
          Rustic chic weddings have become a popular trend among today’s couples. Unlike
          traditional, formal events, rustic themes inspire a sense of warmth and comfort. With
          simple, natural decor and soft color palettes, this wedding style highlights the intimacy
          of romance.
        </p>
        <p>
          If you’re hosting a rustic wedding, choose{' '}
          <a
            href="https://www.zola.com/wedding-planning/save-the-date/shop"
            target="_blank"
            rel="noopener noreferrer"
          >
            save the dates
          </a>{' '}
          that reflect that warmth to your closest friends and family as you invite them to share
          your special day.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'When should I use rustic save the dates to announce my wedding?',
        body: (
          <>
            <p>
              A rustic look can add a down-to-earth touch to any wedding, but this style
              particularly complements:
            </p>
            <p>
              <b>Country Weddings</b> – The epitome of country charm, a barn venue naturally lends
              itself to this wedding style. To further your rustic feel, adorn wooden ceiling
              rafters with fairy lights, natural greenery, and white drapery. Consider coupling this
              look with rustic country save the dates for a truly cohesive wedding theme.
            </p>
            <p>
              <b>Backyard Weddings</b> – Transform your backyard into an enchanting{' '}
              <a
                href="https://www.zola.com/wedding-vendors/search/wedding-venues"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding venue
              </a>{' '}
              with rustic chic decor such as mason jars, antique barrels, and simple wooden benches.
              Hang string lights up and watch your rustic wedding theme come to life as you
              celebrate your love story!
            </p>
            <p>
              <b>Vintage Weddings</b> – Rustic vintage weddings transport your guests back in time
              with simple decor that inspires nostalgia. The delicate lace and copper touches in
              your decor will beautifully match the simple white detailing, foil accents, and wooden
              backgrounds of Zola’s rustic chic save the dates.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do you reflect your personal style in your rustic wedding invitations?',
        body: (
          <>
            <p>
              Search Zola’s broad selection of rustic wedding save the dates to find the style
              that’s right for you, then customize your selection with details that elevate your
              invite from pretty to perfect. As you browse and redesign, consider these important
              design elements:{' '}
            </p>
            <p>
              <b>Color</b> – Search for colors that match the vibe of your upcoming event. You can
              reflect the natural greens, browns, and whites of your rustic decor with an earthy
              color palette, or design country save the dates using vibrant warm tones, like a
              sunset over rolling hills.{' '}
            </p>
            <p>
              <b>Paper Type</b> – Your rustic chic save the dates will look great on either rugged
              natural paper or recycled paper made of organic materials.
            </p>
            <p>
              <b>Custom Photo</b> – You can always personalize your invitations with timeless,
              woodsy, and enchanting engagement photos of you and your spouse-to-be.
            </p>
          </>
        ),
      },
      {
        subheading: 'Plan a Picturesque Wedding with Zola’s Rustic Wedding Invitations',
        body: (
          <>
            <p>
              With a rustic theme, invitation, and decor style, you can capture the simple elegance
              of your love and carry it through every aspect of your wedding. With Zola, you can
              even incorporate it into your invitations, your choice of{' '}
              <a
                href="https://www.zola.com/wedding-vendors"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding vendors
              </a>
              , and your free{' '}
              <a
                href="https://www.zola.com/wedding-planning/website"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding website
              </a>
              .
            </p>
            <p>
              And that’s not the only thing that’s free. When you order your{' '}
              <a
                href="https://www.zola.com/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>{' '}
              through Zola, you’ll receive free guest addressing, and envelopes, as well as optional
              free samples before making your final decision. Rustic weddings may seem effortlessly
              stunning, but they require some real forethought and planning—luckily, you can do it
              all with Zola.
            </p>
          </>
        ),
      },
    ],
  },
  destination: {
    description:
      "Set the tone for your big day with Zola's destination save the dates. Travel-themed templates perfect for any celebration. Get free addressing!",
    summary: (
      <>
        <p>
          The only thing more exciting than a wedding is a destination wedding. You get to combine
          all the best aspects of your wedding day with a tropical vacation, alongside the love of
          your life, your family, and your closest friends. Talk about a win-win-win.
        </p>
        <p>
          To capture the thrill of your upcoming nuptials, and prepare your guests for the trip of a
          lifetime, embrace travel-themed save the dates for your destination wedding.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'How do I design save the dates to match my destination wedding?',
        body: (
          <>
            <p>
              Whether you’re saying “I do” on an exotic island or making it official against a snowy
              mountain backdrop, you can adapt your destination save the dates to your wedding’s
              style using your wedding theme, different wedding stationery layouts, and travel plans
              as inspiration.
            </p>
            <p>Take a look at some idyllic options below:</p>
            <p>
              <b>Beachy</b> – For an oceanfront wedding, many couples embrace the natural, beachy
              style with decor made of driftwood, palm leaves, and sea glass. You can easily
              incorporate these stylistic elements into your invitations with seashell
              embellishments, wood backgrounds, and watercolor waves.
            </p>
            <p>
              <b>Vintage</b> – Why not travel to a different era as well as location with a
              vintage-style destination wedding? If you’re traveling to the Italian countryside or
              the Grecian coast, capitalize on the rustic, vintage aesthetic with invites to match.
            </p>
            <p>
              <b>Bohemian</b> – A whimsical desert wedding begs for bohemian-style flair. If you’re
              replacing your traditional aisle runner with colorful, handwoven rugs and trading in
              floral centerpieces for pampas grass and cacti, your save the dates can match the
              boho-chic aesthetic with warm colors, floral accents, and fresh fonts.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we design the destination wedding invites of our dreams?',
        body: (
          <>
            <p>
              A destination wedding is an amazing way to see something new. For save the dates that
              match the vibe of your location, explore Zola’s{' '}
              <a
                href="https://www.zola.com/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>
              . You’ll have access to a vast array of search filters and customization options such
              as:
            </p>
            <p>
              <b>Color</b> – Find a save the date idea that perfectly matches your wedding palette,
              whether that includes bright pinks and yellows to match a tropical beach locale, or
              mixes pastels with earthy greens for a rustic mountain wedding.{' '}
            </p>
            <p>
              <b>Custom Photo</b> – Give your guests a glimpse into your destination wedding venue
              or use photos of your past adventures to embellish a destination wedding invitation.
            </p>
            <p>
              <b>Foil</b> – Add gold or rose gold foil accents to your invitations to capture the
              enchanting vibes of a bohemian or vintage-style wedding. Alternatively, silver foil
              accents beautifully recreate the way the sun glints off the waves for a beachside
              bash. <i>As if destination weddings needed more shimmer! </i>
            </p>
          </>
        ),
      },
      {
        subheading: 'Zola: Your Destination for the Perfect Wedding Invitations',
        body: (
          <>
            <p>
              You don’t have to travel far to find the best destination wedding{' '}
              <a
                href="https://www.zola.com/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>
              ,{' '}
              <a
                href="https://www.zola.com/wedding-vendors"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding vendors
              </a>
              , and{' '}
              <a
                href="https://www.zola.com/wedding-planning/website"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding websites
              </a>
              . With free free guest addressing, and free envelopes, Zola helps you send your
              destination invites all across the world without stepping outside. You can even select
              up to five free design samples to see in person before placing your final order.
            </p>
            <p>
              With Zola, you’re guaranteed to find save the dates as breathtaking as your
              destination.
            </p>
          </>
        ),
      },
    ],
  },
  beach: {
    description:
      "Set the tone for your big day with Zola's beach save the dates. Oceanside templates perfect for any celebration. Free guest addressing and envelopes!",
    summary: (
      <p>
        From the salty air to the sunshine, it’s no surprise that beach-themed weddings never go out
        of style, and a beautiful wedding deserves a wedding invitation to match. Fortunately, Zola
        has you covered. We have tons of beautiful{' '}
        <a
          href="https://www.zola.com/wedding-planning/save-the-date/shop"
          target="_blank"
          rel="noopener noreferrer"
        >
          save the dates
        </a>{' '}
        to fit any seaside celebration. Users can choose from our handpicked beach-themed designs or
        customize their invites to paradisal perfection.
      </p>
    ),
    faqContent: [
      {
        subheading: 'How can I create beach themed save the dates to match my destination wedding?',
        body: (
          <>
            <p>
              Whether you’re adding seashells to the centerpieces or saying “I do” with your toes in
              the sand, there are plenty of ways to plan a tropical wedding. The most obvious option
              is a true beach wedding, with the ocean waves and tropical sunset as the backdrop for
              both your vows and your save-the-date designs.{' '}
            </p>
            <p>
              But you don’t need to be <i>at</i> the beach to draw style inspiration from the sea,
              sand, and sky.
            </p>
            <p>
              You can transform any outdoor locale with palm trees, tropical flowers, and airy,
              lightweight decor for a vibrant, beachy look. Beach-themed invites are great for
              summer weddings, too. That way, the season will be right even if the setting isn’t.
              Tie in your save the dates by choosing one of Zola’s modern botanical or tropical
              designs.
            </p>
          </>
        ),
      },
      {
        subheading: 'Beachy Save the Date Ideas',
        body: (
          <>
            <p>
              Your beach wedding in paradise can’t be all laid-back vibes all the time. Your guests
              still need to know their final destination with enough notice to plan ahead. Don’t
              forget to fill them in on the details—with stylish beach-themed save the dates for
              your big day, of course:{' '}
            </p>
            <p>
              <b>Paper Type</b> – Beachy fabrics are light and airy. The same can be said of your
              save the dates if you opt for Zola’s equally lightweight linen paper option. That way,
              your{' '}
              <a
                href="https://www.zola.com/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>{' '}
              won’t just <i>look</i> beachy, they’ll feel it, too.
            </p>
            <p>
              <b>Designs</b> – Zola offers dozens of beach and destination templates to fit any
              oceanside wedding. From anchor motifs for nautical nuptials to palm tree designs for a
              seashore ceremony, we’ve got styles for all types of beach weddings.
            </p>
            <p>
              <b>Color</b> – Beach weddings can incorporate a wide spectrum of shades, beyond the
              standard blue and white. From sunset pinks to vibrant corals, Zola’s selection of bold
              save the dates are designed to match any color palette.
            </p>
          </>
        ),
      },
      {
        subheading: 'Surf, Sand, and Save the Dates: Find Beach-Themed Wedding Invitations at Zola',
        body: (
          <>
            <p>
              Choose from dozens of tropical, beach, and ocean-themed save the dates, as well as
              matching wedding programs and{' '}
              <a
                href="https://www.zola.com/wedding-planning/website"
                target="_blank"
                rel="noopener noreferrer"
              >
                free wedding websites
              </a>
              , and don’t forget to build your custom{' '}
              <a
                href="https://www.zola.com/wedding-registry"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding registry
              </a>
              . Between Zola’s easy-to-use invitation templates, our comprehensive selection of{' '}
              <a
                href="https://www.zola.com/wedding-vendors"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding vendors
              </a>{' '}
              for any location, and the ultimate{' '}
              <a
                href="https://www.zola.com/wedding-planning/checklist"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding planning checklist
              </a>
              , your special wedding day will feel like a true vacation for you and your wedding
              guests.
            </p>
            <p>
              Don’t forget the sunscreen, because you’re in for a long day of celebration when you
              plan your beach wedding with Zola!
            </p>
          </>
        ),
      },
    ],
  },
  boho: {
    description:
      "Set the tone for your big day with Zola's boho save the dates. Soft, romantic templates perfect for any celebration. Get free guest addressing and more!",
    summary: (
      <p>
        It’s no surprise that bohemian weddings are becoming increasingly popular. Boho’s soft,
        romantic vibe is the perfect theme for a day of love. If you’re looking for style tips and
        ways to incorporate a boho theme, you’ve come to the right place. Zola has hundreds of
        beautiful designs to help you find your perfect bohemian{' '}
        <a
          href="https://www.zola.com/wedding-planning/save-the-date/shop"
          target="_blank"
          rel="noopener noreferrer"
        >
          save the dates
        </a>
        .
      </p>
    ),
    faqContent: [
      {
        subheading: 'How do I create a bohemian wedding with the perfect boho invites to match?',
        body: (
          <>
            <p>
              There’s a reason boho weddings are all the rage right now: the breathtaking decor and
              immaculate ambiance. This trendy aesthetic looks stunning in real life, just as it
              does on the printed paper of your save the dates. Use these style tips to create the
              ultimate bohemian wedding (and the bohemian save the dates to match):
            </p>
            <p>
              <b>Botanical Greenery</b> – Boho weddings embrace the surrounding nature, and what’s
              more natural than plants? Incorporate delicate flowers and greenery into your decor,
              and tie everything together with a botanical save the date from Zola.
            </p>
            <p>
              <b>Earthy Tones</b> – Bohemian weddings are often filled with effortless foliage,
              rustic wooden decor, and white accents. These muted yet trendy hues create a perfect
              base color palette for your wedding and invites. Accent colors in delicate shades of
              lilac, apricot, and blush can provide much-needed pops of color for your save the
              dates.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can I create the perfect bohemian save the dates?',
        body: (
          <>
            <p>
              There are plenty of suitable designs for boho invites for your big day, but a truly
              bohemian lifestyle embraces freedom and authenticity. You can customize Zola’s
              existing{' '}
              <a
                href="https://www.zola.com/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>{' '}
              to create the most “boho” save the date of all, the one that speaks to <i>you</i>:
            </p>
            <p>
              <b>Paper Type</b> – Bohemian decor is usually lightweight and flowy. Embrace that
              untethered feeling with linen or eggshell stationery, or choose eco-friendly recycled
              paper, perfect for rustic weddings and botanical designs.
            </p>
            <p>
              <b>Custom Photo</b> – Show off your bohemian theme by adding a custom photo. This
              detail will help personalize your theme, plus it’s a great excuse to have a fun, boho
              photoshoot (not that you needed one).
            </p>
            <p>
              <b>Style</b> – Zola has a wide selection of handpicked bohemian invitation designs.
              With whimsical fonts and delicate details, these boho save the dates will fully embody
              this breezy, beautiful aesthetic <i>and</i> your free spirit.
            </p>
          </>
        ),
      },
      {
        subheading:
          'Earthy, Ethereal, and Enchanting: Find Your Bohemian Wedding Save the Dates at Zola',
        body: (
          <>
            <p>
              For the perfect balance of nature and nuptials, Zola is here for all your boho wedding
              needs.
            </p>
            <p>
              Choose from dozens of bohemian, rustic, and floral save the date designs to match your
              wedding theme. Couples can also take advantage of our{' '}
              <a
                href="https://www.zola.com/expert-advice/checklist/your-ultimate-wedding-planning-checklist"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding checklist
              </a>
              , matching wedding programs,{' '}
              <a
                href="https://www.zola.com/wedding-registry"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding registry
              </a>{' '}
              services, and{' '}
              <a
                href="https://www.zola.com/wedding-planning/website"
                target="_blank"
                rel="noopener noreferrer"
              >
                free wedding websites
              </a>
              . Embrace your inner forest nymph, and plan your magical boho wedding with Zola!
            </p>
          </>
        ),
      },
    ],
  },
  simple_minimalist: {
    description:
      "Set the tone for your big day with Zola's simple save the dates. Minimalistic templates perfect for any celebration. Get free envelopes and more!",
    summary: (
      <>
        <p>
          Planning a wedding is complex. There’s a variety of{' '}
          <a
            href="https://www.zola.com/wedding-vendors/search/wedding-venues"
            target="_blank"
            rel="noopener noreferrer"
          >
            wedding venues
          </a>{' '}
          to explore, catering menus to taste, and colors to decide on. Fortunately, there’s one
          aspect of your wedding that can remain straightforward: your{' '}
          <a
            href="https://www.zola.com/wedding-planning/save-the-date/shop"
            target="_blank"
            rel="noopener noreferrer"
          >
            save the dates
          </a>
          .
        </p>
        <p>
          With simple designs that convey a light, uncluttered feel, basic save the dates offer a
          breath of fresh air amidst your busy{' '}
          <a href="https://www.zola.com/wedding-planning" target="_blank" rel="noopener noreferrer">
            wedding planning
          </a>
          . If you use Zola as your design lab, the only tough decision you’ll have to make is who’s
          lucky enough to receive one.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'How can I incorporate simple save the dates into my big day?',
        body: (
          <>
            <p>
              Simple wedding save the dates are an understated option that find their flash within
              their content: the announcement of your big day. Their timeless look makes these save
              the dates the perfect choice if your wedding style is:
            </p>
            <p>
              <b>Modern</b> – With a focus on clean lines and geometric shapes, minimalist save the
              dates exude style while maintaining focus on the happy couple and the special day.
            </p>
            <p>
              <b>Traditional</b> – Simple black lettering against a white background is a classic
              look that will age as gracefully as you and your spouse-to-be.{' '}
            </p>
            <p>
              <b>Undecided</b> – If you haven’t decided on a style yet, don’t worry. The simplicity
              of basic save the dates means they can easily complement <i>any</i> wedding style, so
              you don’t have to rush into making all the decisions before you’re ready.
            </p>
            <p>
              By avoiding colored inks, intricate designs, and elaborate accents such as beveled
              edging, gold foil, or intricate letterpress, basic wedding invitations can also
              complement your budget. If your wedding guest list could line an aisle, or if you want
              to dedicate more of your budget to reception decor, choose simple, elegant save the
              dates.
            </p>
          </>
        ),
      },
      {
        subheading: 'How can I incorporate my personal style into simple save the dates?',
        body: (
          <>
            <p>
              With Zola’s online invitation shop, you have access to a wide selection of
              pre-designed{' '}
              <a
                href="https://www.zola.com/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>
              . Once you’ve found your favorite styles with our easy-to-use filters, you can further
              personalize your invites. Keep these style tips in mind as you do:
            </p>
            <p>
              <b>Style</b> – For simplicity, choose a minimalist, modern, or classic design. These
              styles will guarantee elegance without going over-the-top.
            </p>
            <p>
              <b>Color</b> – Monochromatic color schemes are the epitome of simplicity. This kind of
              palette will lend itself beautifully to your basic invites, but you can also accent a
              classic black and white invitation with a single pop of color.
            </p>
          </>
        ),
      },
      {
        subheading: 'Finding Your Perfect Save the Dates Is As Simple As Z-O-L-A',
        body: (
          <>
            <p>
              For the best collection of simple save the dates and easiest personalization options,
              choose Zola. You can even receive free samples ahead of time to perfect your design
              long before the big day. Plus, with free free guest addressing, and free envelopes,
              Zola makes finding and mailing your wedding invitations as simple as saying, “I do.”
            </p>
          </>
        ),
      },
    ],
  },
  modern: {
    description:
      "Set the tone for your big day with Zola's modern save the dates. Contemporary templates perfect for any celebration. Get free guest addressing!",
    summary: (
      <>
        <p>
          Weddings may be traditional affairs but that doesn’t mean you can’t bring your big day
          into the 21st century with modern designs and a contemporary aesthetic. If you’re now
          looking for save the dates to match, Zola has hundreds of beautiful, customizable options.
        </p>
        <p>
          Our website is equipped with the best tools to help couples plan their big day. Users can
          look through our large selection of modern save the date designs and choose their
          favorite, or customize their own.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'How do I plan a modern wedding with save the dates to match?',
        body: (
          <>
            <p>
              Our world has embraced modernity, but weddings rarely stray from tradition. If you’re
              interested in breaking the mold and planning a new-age nuptial, Zola has you covered.
            </p>
            <p>
              Below are some stylish tips for planning a modern wedding with equally contemporary
              save the dates:
            </p>
            <p>
              <b>Greenery</b> – With the influx of plants as home decor, it’s no surprise that 2021
              is the year of green weddings. Modern couples are swapping traditional floral bouquets
              for striking greenery in their centerpieces. The best part? Your save the date can
              match this on-trend aesthetic and wedding style with Zola’s modern botanical designs.
            </p>
            <p>
              <b>Simplicity</b> – Minimalist weddings are in, and they start with minimalist wedding
              stationery. Modern designs are moving toward a simpler look, and weddings are no
              different. Utilize negative space, clean lines, and monochromatic palettes as you tie
              your modern wedding date invitation design into your overall celebration.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do I create modern save the dates that fit my style?',
        body: (
          <>
            <p>
              Modern invites may be clean, crisp, and simple, but that doesn’t mean they should be
              impersonal. When designing your save the dates, Zola has plenty of customizable
              options to create the perfect mix of modern and <i>mine</i> that encapsulates your
              marriage-to-be:
            </p>
            <p>
              <b>Custom Photo</b> – Why not show off those dazzling engagement photos on your save
              the date card? Zola couples can choose from dozens of modern save the dates that
              incorporate custom photographs.
            </p>
            <p>
              <b>Typography</b> – Zola’s modern save the date designs offer a broad spectrum of
              interesting fonts that go beyond the usual cursive and calligraphy. From easy-going
              script to bold print, these options will bring any design up to date. You can also
              experiment with ink color!
            </p>
            <p>
              <b>Color</b> – Many of today’s couples are embracing change with bold, bright, and
              unexpected hues into their date design. With Zola’s beautiful selection of modern
              designs, you’ll have no trouble matching your wedding day color palette to your save
              the dates. Templates come in a wide variety of tones, including vivid teal, deep
              orange, and baby pink.
            </p>
          </>
        ),
      },
      {
        subheading: 'Update Your Style With Zola’s Modern Designs',
        body: (
          <>
            <p>
              Sending out the perfect save the dates has never been easier with Zola. With hundreds
              of beautiful designs, personalized options, and modern flair to match, you’ll be
              wishing you could send them to <i>more</i> people.
            </p>
            <p>
              As you embrace modern styles this season, don’t forget to embrace modern technology,
              too, with Zola’s comprehensive collection of{' '}
              <a
                href="https://www.zola.com/wedding-vendors"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding vendors
              </a>
              , expert advice and styling tips, and{' '}
              <a
                href="https://www.zola.com/wedding-planning/website"
                target="_blank"
                rel="noopener noreferrer"
              >
                free wedding websites
              </a>{' '}
              to match your contemporary{' '}
              <a
                href="https://www.zola.com/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>{' '}
              — and your contemporary life.
            </p>
          </>
        ),
      },
    ],
  },
  vintage_retro: {
    description:
      "Set the tone for your big day with Zola's vintage save the dates. Nostalgic templates perfect for any celebration. Get free envelopes and addressing!",
    summary: (
      <>
        <p>
          True love is timeless. And with the right vintage touches, your wedding can be too.
          Antique furniture, lace accents, and family heirlooms can all be incorporated into a
          vintage-style wedding that transports your guests through time.
        </p>
        <p>
          If you and your significant other are planning a retro wedding, Zola can help you design
          the perfect vintage-style{' '}
          <a
            href="https://www.zola.com/wedding-planning/save-the-date/shop"
            target="_blank"
            rel="noopener noreferrer"
          >
            save the dates
          </a>{' '}
          to match.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'What kind of wedding themes work with vintage save the dates?',
        body: (
          <>
            <p>
              With vintage weddings, you have decades of inspiration to pull from. In fact, vintage
              wedding save the dates can match a number of old-school styles, including:{' '}
            </p>
            <p>
              <b>Bohemian</b> – Crocheted fabrics and colorful wildflowers capture the boho hippy
              vibes of the ‘60s.{' '}
            </p>
            <p>
              <b>Retro</b> – With a big band performance and nostalgic photo booth, you and your
              guests can jive into the ‘50s.
            </p>
            <p>
              <b>Rustic</b> – Journey to a simpler time through the rustic charm of wooden tables,
              lace linens, and natural greenery.
            </p>
            <p>
              <b>Whimsical</b> – With Persian rugs, mismatched chairs, and antique china, your
              vintage wedding can feel like something out of a storybook.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do I create the perfect vintage save the dates to match my theme?',
        body: (
          <>
            <p>
              With Zola’s various invitation and save the date options, it’s never been easier to
              find the perfect match for your wedding’s vintage vibe. You can search through and
              even customize Zola’s pre-designed save the dates according to:
            </p>
            <p>
              <b>Color</b> – Browse based on your color palette, whether that means the grooviest
              hues or the loveliest old-fashioned shades. For retro save the dates, choose bold
              cherry reds, neon pinks, and bright yellows to capture that vibrant, old-school
              energy.
            </p>
            <p>
              <b>Custom Photo</b> – Lean into the nostalgia of a vintage wedding invitation by
              adding your own black and white engagement photos, or amplify the retro fun with a
              picture of the two of you in decade-specific attire.
            </p>
            <p>
              <b>Foil</b> – For a whimsical vintage theme, add gold, rose gold, or silver foil
              accents to your invitation text for a nice balance of tinsel and timeless.
            </p>
            <p>
              <b>Paper Type</b> – Choose Zola’s recycled paper option to highlight the raw, natural
              charm of rustic vintage save the dates, or opt for pearlescent paper to add some
              shimmer.
            </p>
          </>
        ),
      },
      {
        subheading: 'For Vintage Invitations With Modern Solutions, Choose Zola',
        body: (
          <>
            <p>
              Regardless of which decade speaks to you and your love, Zola can help you create a
              timeless vintage wedding. Not sure which style is right for you? With Zola, you can
              order free samples of up to five different save the date designs. That way, you’re
              guaranteed to find vintage{' '}
              <a
                href="https://www.zola.com/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>{' '}
              that stand the test of time.
            </p>
          </>
        ),
      },
    ],
  },
  watercolor: {
    description:
      "Set the tone for your big day with Zola's watercolor save the dates. Artistic templates perfect for any celebration. Get free guest addressing!",
    summary: (
      <>
        <p>
          Watercolor wedding invitations have become a popular trend in recent years. If you’ve been
          lucky enough to receive one, you can understand why. Not only do watercolors create a
          sense of texture and movement, but their delicate brush strokes invoke a dreamy
          romanticism. With a watercolor invitation, your guests know to expect nothing short of a
          fairytale on your wedding day.
        </p>
        <p>
          And with Zola’s easy-to-use search filters and customizable design options, you’ll feel
          like you gained a fairy godmother in your{' '}
          <a href="https://www.zola.com/wedding-planning" target="_blank" rel="noopener noreferrer">
            wedding planning
          </a>{' '}
          process.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'How do I incorporate a watercolor invitation into my wedding?',
        body: (
          <>
            <p>
              Naturally soft and ethereal, watercolors can add magic to any wedding style or theme.
              Choose watercolors to complement a wedding that is:{' '}
            </p>
            <p>
              <b>Whimsical &amp; Bohemian</b> – Planning on filling your secret garden{' '}
              <a
                href="https://www.zola.com/wedding-vendors/search/wedding-venues"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding venue
              </a>{' '}
              with delicate details and flowy fabrics? The light brush strokes and subdued colors of
              a watercolor design will match this perfectly.
            </p>
            <p>
              <b>Formal &amp; Romantic</b> – Watercolor may not be the most traditional option for a
              romantic black-tie wedding, but that’s why it’s such a great choice. The vibrancy of a
              watercolor invite will add a pop of color to an otherwise monochromatic design.
            </p>
            <p>
              <b>Beachy</b> – Looking to signal a destination wedding without resorting to palm
              trees and seashells? The watercolor waves add a classy touch to any tropical beach
              wedding.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do I create the perfect watercolor save the dates for my big day?',
        body: (
          <>
            <p>
              If you choose watercolor{' '}
              <a
                href="https://www.zola.com/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>
              , you’re not limited to simple streaks of color. There are plenty of wedding
              invitation stationery, envelopes, and custom design options to choose from. Explore
              watercolor patterns, florals, and landscapes to make your invitations stand out.{' '}
            </p>
            <p>
              Zola also offers unique personalization options for a wedding date card, including:{' '}
            </p>
            <p>
              <b>Color</b> – Water may be blue but watercolors can be any shade of the rainbow. For
              a romantic wedding, choose pastel pinks and dreamy purples. For a beach wedding, try
              streaks of blue, teal, or green to mimic the flow of the ocean. For a garden wedding,
              use lush emeralds and splashes of gold to echo the beauty of nature.{' '}
            </p>
            <p>
              <b>Paper Type</b> – Thick, textured papers, like eggshell or natural, add structure
              and dimension to your save the dates, further transforming them into true works of
              art.
            </p>
          </>
        ),
      },
      {
        subheading: 'Set the Tone for Your Special Day with Zola',
        body: (
          <>
            <p>
              Once you’ve found your venue and set the date, Zola can help you customize the perfect
              watercolor{' '}
              <a
                href="https://www.zola.com/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitations
              </a>{' '}
              and free{' '}
              <a
                href="https://www.zola.com/wedding-planning/website"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding website
              </a>
              . Plus, you can receive free samples of your five favorite designs before you order.
              That way, you’re guaranteed the exact look and feel you want.
            </p>
            <p>
              To captivate your guests, capture the beauty of your special day, and paint the
              wedding of your dreams, choose Zola.
            </p>
          </>
        ),
      },
    ],
  },
  disney: {
    description:
      "Set the tone for your big day with Zola's Disney save the dates. Magical templates perfect for any celebration. Get free envelopes and more!",
  },
  dusty_blue: {
    description:
      "Set the tone for your big day with Zola's dusty blue save the dates. Cool, refined templates perfect for any celebration. Get free addressing!",
  },
  royal_blue: {
    description:
      "Set the tone for your big day with Zola's royal blue save the dates. Classic templates perfect for any celebration. Get free addressing and envelopes!",
  },
  navy: {
    description:
      "Set the tone for your big day with Zola's navy blue save the dates. Timeless templates perfect for any celebration. Get free guest addressing!",
  },
  burgundy: {
    description:
      "Set the tone for your big day with Zola's burgundy save the dates. Rich, berry hue templates perfect for any celebration. Free guest addressing!",
  },
  lavender: {
    description:
      "Set the tone for your big day with Zola's lavender save the dates. Tranquil, loving templates perfect for any celebration. Free guest addressing!",
  },
  plum: {
    description:
      "Set the tone for your big day with Zola's plum save the dates. Regal, elegant templates perfect for any celebration. Get free addressing and more!",
  },
  rose_gold: {
    description:
      "Set the tone for your big day with Zola's rose gold save the dates. Exquisite, upscale templates perfect for any celebration. Get free envelopes!",
  },
  dusty_rose: {
    description:
      "Set the tone for your big day with Zola's dusty rose save the dates. Modern, romantic templates perfect for any celebration. Get free addressing!",
  },
  coral: {
    description:
      "Set the tone for your big day with Zola's coral save the dates. Dynamic, vibrant templates perfect for any celebration. Get free guest addressing!",
  },
  peach: {
    description:
      "Set the tone for your big day with Zola's peach save the dates. Warm, trendy templates perfect for any celebration. Get free addressing and envelopes!",
  },
  emerald_green: {
    description:
      "Set the tone for your big day with Zola's emerald green save the dates. Rich, jewel tone templates perfect for any celebration. Free addressing!",
  },
  teal: {
    description:
      "Set the tone for your big day with Zola's teal save the dates. Unique, elegant templates perfect for any celebration. Get free envelopes!",
  },
  black: {
    description:
      "Set the tone for your big day with Zola's black save the dates. Elegant templates perfect for any celebration. Free guest addressing and envelopes!",
  },
  blue: {
    description:
      "Set the tone for your big day with Zola's blue save the dates. Faithful templates perfect for any celebration. Free guest addressing and envelopes!",
  },
  brown: {
    description:
      "Set the tone for your big day with Zola's brown save the dates. Modern, rustic templates perfect for any celebration. Get free guest addressing!",
  },
  green: {
    description:
      "Set the tone for your big day with Zola's green save the dates. Soft, earthy templates perfect for any celebration. Get free guest addressing!",
  },
  grey: {
    description:
      "Set the tone for your big day with Zola's grey save the dates. Upscale, modern templates perfect for any celebration. Get free guest addressing!",
  },
  orange: {
    description:
      "Set the tone for your big day with Zola's orange save the dates. Bright, joyful templates perfect for any celebration. Get free guest addressing!",
  },
  pink: {
    description:
      "Set the tone for your big day with Zola's pink save the dates. Romantic, warm templates perfect for any celebration. Get free guest addressing!",
  },
  purple: {
    description:
      "Set the tone for your big day with Zola's purple save the dates. Lush, royal templates perfect for any celebration. Get free guest addressing!",
  },
  red: {
    description:
      "Set the tone for your big day with Zola's red save the dates. Bold, passionate templates perfect for any celebration. Get free envelopes!",
  },
  white: {
    description:
      "Set the tone for your big day with Zola's white save the dates. Pure, delicate templates perfect for any celebration. Get free guest addressing!",
  },
  yellow: {
    description:
      "Set the tone for your big day with Zola's yellow save the dates. Bright, beautiful templates perfect for any celebration. Get free envelopes and more!",
  },
};
